import { useState, useEffect, useCallback } from 'react';
import { API } from '@/utils/API';
import { Document } from '@/hooks/submissions/useSubmissionDocuments';

interface SiteDocumentsResponse {
  site_id: string;
  documents: Document[];
}

interface UseSiteDocumentsReturn {
  documents: Document[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useSiteDocuments = (siteId: string | null): UseSiteDocumentsReturn => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDocuments = useCallback(async () => {
    if (!siteId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await API.authenticated.fetch(`/sites/${siteId}/documents`);

      if (!response.ok) {
        throw new Error('Failed to fetch documents');
      }

      const data: SiteDocumentsResponse = await response.json();
      setDocuments(data.documents || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load documents');
    } finally {
      setIsLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    if (siteId) {
      fetchDocuments();
    }
  }, [siteId, fetchDocuments]);

  return { documents, isLoading, error, refetch: fetchDocuments };
};
